import * as yup from 'yup';
import { BusinessEntity, CreateBusinessEntityVariablesForForm, MetadataField } from './types';
import { IObjectRaw, ObjectProperty } from '../CustomObjects/types';
import { RegisterOrUpdateDynamicObjectsWithValuesPayload } from 'views/TenantProfileSettings/components/types';

export const defaultValues: CreateBusinessEntityVariablesForForm = {
    displayName: '',
    description: '',
    entityTypesIds: [],
    enabled: true,
};

export const createSchema = (existingEntities: BusinessEntity[] = [], currentEntityId?: string) =>
    yup
        .object({
            displayName: yup
                .string()
                .trim()
                .transform((value) => (typeof value === 'string' ? value.trim() : value))
                .min(3, 'Name must be at least 3 characters')
                .max(100, 'Entity names cannot exceed 100 characters.')
                .matches(/^[a-zA-Z0-9\s]*$/, 'Entity names can only contain letters, numbers, and spaces.')
                .test('no-duplicate', 'This Entity name already exists.', (value) => {
                    if (!value) return true;
                    const trimmedValue = value.trim().toLowerCase();
                    // Logic to avoid name duplication considering representedTenant
                    // Exclude the current entity from the duplicate check
                    const duplicateEntity = existingEntities.find((entity) => {
                        // Skip the current entity being edited
                        if (currentEntityId && entity.id === currentEntityId) {
                            return false;
                        }
                        // If the current entity exists, get its representedTenant for comparison
                        const currentEntity = existingEntities.find((e) => e.id === currentEntityId);
                        const currentRepresentedTenant = currentEntity?.representedTenant;

                        const entityName = entity.displayName.trim().toLowerCase();
                        // If both have representedTenant, names must match and tenants must match
                        if (entity.representedTenant && currentRepresentedTenant) {
                            return entityName === trimmedValue && entity.representedTenant.id === currentRepresentedTenant.id;
                        }
                        // If neither have representedTenant, only match names
                        if (!entity.representedTenant && !currentRepresentedTenant) {
                            return entityName === trimmedValue;
                        }
                        // If only one has representedTenant, they're not duplicates
                        return false;
                    });
                    return !duplicateEntity;
                })
                .required(),
            entityTypesIds: yup.array().min(1, 'Entity Type is a required field').required('Entity Type is a required field')
        })
        .required();

// Keep backward compatibility
export const schema = createSchema();

export const normalizeItemToForm = (item: BusinessEntity) => {
    const baseObj = {
        displayName: item.displayName || '',
        description: item.description || '',
        entityTypesIds: item.entityTypes.map(({ id }) => +id),
        enabled: item.enabled,
        customDisplayName: item.customDisplayName || '',
        einNumber: item.einNumber || '',
        taxIdentificationNum: item.taxIdentificationNum || '',
        freightThreshold: item.freightThreshold,
        vendorNumber: item.vendorNumber,
        customerNumber: item.customerNumber,
        productThreshold: item.productThreshold,
        legalName: item.legalName,
        website: item.website,
        ownerTenantId: +(item.ownerTenant?.id || 0),
        keyIdentifiers: item.keyIdentifiers,
        password: item.password,
        geographicLocationId: item.geographicLocation?.id ? +item.geographicLocation.id : null,
        tagline: item.tagline
    } as CreateBusinessEntityVariablesForForm;

    if (item.freightThreshold) baseObj.freightThreshold = item.freightThreshold;
    if (item.apContact?.id) baseObj.apContactId = +item.apContact.id;
    if (item.arContact?.id) baseObj.apContactId = +item.arContact.id;
    if (item.address?.id) baseObj.addressId = +item.address.id;

    return baseObj;
};

/**
 * Returns the properties by id for the given metadata field
 *
 * @param customObjs {IObjectRaw[]} Objects stored by the entity
 * @param metadataField {MetadataField}
 * @returns
 */
export const getPropertiesById = (customObjs: IObjectRaw[] | undefined, metadataField: MetadataField): Record<number, ObjectProperty> => {
    const selectedObj = customObjs?.find((el) => el.id === metadataField?.objectDefinition?.id);
    return selectedObj?.objectProperties.filter((el) => el.enabled).reduce((acc, el) => ({ ...acc, [+el.id]: el }), {}) || {};
};

export const notAllowedKeys = ['ownerTenantId', 'geographicalLocation'];
/**
 * Generates a payload object of type RegisterOrUpdateDynamicObjectsWithValuesPayload.
 *
 * @param {number} objectDefinitionId - The ID of the object definition.
 * @param {number} id - The ID of the object instance.
 * @param {Record<string, any>} objectValues - An object mapping property IDs to their values.
 * @returns {RegisterOrUpdateDynamicObjectsWithValuesPayload} The payload to be used for registering or updating a dynamic object.
 */
export const generateBusinessObjectToAtribute = (objectDefinitionId: number, objectValues: Record<string, any>, id?: number) => {
    const values = Object.entries(objectValues).map(([key, value]) => ({ propertyId: +key, value }));

    const obj: RegisterOrUpdateDynamicObjectsWithValuesPayload = {
        data: { objectDefinitionId, id, values }
    };

    return obj;
};

export const objectNameToFieldNameMapping = {
    Address: 'address',
    'AR Contact': 'arContact',
    'AP Contact': 'apContact'
};

export const contactsObjectToFieldMapping = { 'AR Contact': 'arContact', 'AP Contact': 'apContact' };
